import React, { useState, useCallback } from 'react';
import { View, Text, Pressable, Modal, ScrollView, TextInput, ActivityIndicator } from 'react-native';
import { MaterialCommunityIcons, MaterialIcons } from '@expo/vector-icons';
import { XStack, YStack } from 'tamagui';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import apis from '@api/api';

interface Calendar {
  _id: string;
  calendarId: string;
  title: string;
  description?: string;
  timeZone: string;
  color: string;
  availability: {
    monday?: { begin: number; end: number };
    tuesday?: { begin: number; end: number };
    wednesday?: { begin: number; end: number };
    thursday?: { begin: number; end: number };
    friday?: { begin: number; end: number };
    saturday?: { begin: number; end: number };
    sunday?: { begin: number; end: number };
    increments?: number;
  };
}

interface CalendarSelectorProps {
  isVisible: boolean;
  onClose: () => void;
  onSelect: (calendarId: string) => void;
  flowId: string;
}

/**
 * CalendarSelector Modal Component
 * Allows users to browse and select from calendars for appointment scheduling
 */
const CalendarSelector: React.FC<CalendarSelectorProps> = ({
  isVisible,
  onClose,
  onSelect,
  flowId
}) => {
  const theme = themeSelector();
  const [searchQuery, setSearchQuery] = useState('');
  const [calendars, setCalendars] = useState<Calendar[]>([]);
  const [loading, setLoading] = useState(false);

  const colors = {
    background: theme ? '#ffffff' : '#1e293b',
    surface: theme ? '#f8f9fa' : '#2d2d2d',
    border: theme ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.1)',
    text: theme ? '#333333' : '#e0e0e0',
    secondaryText: theme ? '#64748b' : '#94a3b8',
    accent: theme ? '#7c3aed' : '#a78bfa'
  };

  // Fetch calendars when modal opens
  React.useEffect(() => {
    if (isVisible && flowId) {
      fetchCalendars();
    }
  }, [isVisible, flowId]);

  const fetchCalendars = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apis.getCalendarsForFlow(flowId);
      if (response && response.success && Array.isArray(response.data)) {
        setCalendars(response.data);
      } else {
        console.warn('Invalid calendars response format');
        setCalendars([]);
      }
    } catch (error) {
      console.error('Error fetching calendars:', error);
      setCalendars([]);
    } finally {
      setLoading(false);
    }
  }, [flowId]);

  const filteredCalendars = calendars.filter(cal =>
    cal.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    cal.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSelect = useCallback((calendarId: string) => {
    onSelect(calendarId);
    onClose();
  }, [onSelect, onClose]);

  const formatTime = (hour: number) => {
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
    return `${displayHour}${ampm}`;
  };

  return (
    <Modal
      visible={isVisible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <View style={{
        flex: 1,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 20
      }}>
        <View style={{
          width: '100%',
          maxWidth: 600,
          maxHeight: '80%',
          backgroundColor: colors.background,
          borderRadius: 16,
          overflow: 'hidden'
        }}>
          {/* Header */}
          <View style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: 20,
            borderBottomWidth: 1,
            borderBottomColor: colors.border
          }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
              <MaterialCommunityIcons name="calendar-search" size={24} color={colors.accent} />
              <Text style={{ fontSize: 18, fontWeight: '700', color: colors.text }}>
                Select Calendar
              </Text>
            </View>
            <Pressable onPress={onClose} style={{ padding: 4 }}>
              <MaterialIcons name="close" size={24} color={colors.secondaryText} />
            </Pressable>
          </View>

          {/* Search */}
          <View style={{ padding: 16, borderBottomWidth: 1, borderBottomColor: colors.border }}>
            <View style={{
              flexDirection: 'row',
              alignItems: 'center',
              backgroundColor: colors.surface,
              borderRadius: 8,
              paddingHorizontal: 12,
              paddingVertical: 10,
              gap: 8
            }}>
              <MaterialIcons name="search" size={20} color={colors.secondaryText} />
              <TextInput
                value={searchQuery}
                onChangeText={setSearchQuery}
                placeholder="Search calendars..."
                placeholderTextColor={colors.secondaryText}
                style={{
                  flex: 1,
                  fontSize: 14,
                  color: colors.text
                }}
              />
            </View>
          </View>

          {/* Calendars List */}
          <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: 16, gap: 12 }}>
            {loading ? (
              <View style={{ padding: 40, alignItems: 'center' }}>
                <ActivityIndicator size="large" color={colors.accent} />
                <Text style={{ marginTop: 12, fontSize: 14, color: colors.secondaryText }}>
                  Loading calendars...
                </Text>
              </View>
            ) : filteredCalendars.length === 0 ? (
              <View style={{ padding: 40, alignItems: 'center' }}>
                <MaterialCommunityIcons name="calendar-remove" size={48} color={colors.secondaryText} />
                <Text style={{ marginTop: 12, fontSize: 14, color: colors.secondaryText, textAlign: 'center' }}>
                  {searchQuery ? 'No calendars match your search' : 'No calendars available'}
                </Text>
              </View>
            ) : (
              filteredCalendars.map(cal => (
                <Pressable
                  key={cal._id}
                  onPress={() => handleSelect(cal.calendarId)}
                  style={{
                    backgroundColor: colors.surface,
                    padding: 16,
                    borderRadius: 12,
                    borderWidth: 1,
                    borderColor: colors.border,
                    borderLeftWidth: 4,
                    borderLeftColor: cal.color
                  }}
                >
                  <View style={{ flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                    <View style={{ flex: 1 }}>
                      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                        <View style={{
                          width: 12,
                          height: 12,
                          borderRadius: 6,
                          backgroundColor: cal.color
                        }} />
                        <Text style={{ fontSize: 16, fontWeight: '600', color: colors.text }}>
                          {cal.title}
                        </Text>
                      </View>

                      {cal.description && (
                        <Text style={{ fontSize: 13, color: colors.secondaryText, marginBottom: 8 }}>
                          {cal.description}
                        </Text>
                      )}

                      <XStack gap={8} flexWrap="wrap">
                        <View style={{
                          flexDirection: 'row',
                          alignItems: 'center',
                          gap: 4,
                          backgroundColor: theme ? '#dbeafe' : '#1e40af',
                          paddingHorizontal: 8,
                          paddingVertical: 4,
                          borderRadius: 6
                        }}>
                          <MaterialCommunityIcons name="clock-outline" size={12} color={theme ? '#1e40af' : '#93c5fd'} />
                          <Text style={{ fontSize: 11, fontWeight: '600', color: theme ? '#1e40af' : '#93c5fd' }}>
                            {cal.availability.increments || 30}min slots
                          </Text>
                        </View>

                        {cal.availability.monday && (
                          <View style={{
                            backgroundColor: theme ? '#f1f5f9' : '#334155',
                            paddingHorizontal: 8,
                            paddingVertical: 4,
                            borderRadius: 6
                          }}>
                            <Text style={{ fontSize: 11, color: colors.secondaryText }}>
                              {formatTime(cal.availability.monday.begin)}-{formatTime(cal.availability.monday.end)}
                            </Text>
                          </View>
                        )}
                      </XStack>
                    </View>
                    <MaterialIcons name="chevron-right" size={24} color={colors.secondaryText} />
                  </View>
                </Pressable>
              ))
            )}
          </ScrollView>
        </View>
      </View>
    </Modal>
  );
};

export default CalendarSelector;

